import { z } from "zod"

/**
 * BI widget render contract — the SINGLE SOURCE OF TRUTH for the shape a
 * widget renders from, shared by the frontend renderer and the backend
 * validator (STK-62).
 *
 * Zod-first by design (a deviation from the repo's usual types-first split):
 * the schema is the one definition and the TS types are `z.infer`-derived, so
 * the render type and the validation schema can never drift. The frontend's
 * recharts renderers consume the inferred `WidgetSpec`; the widget service
 * validates a query's result against these schemas when a query is bound to a
 * widget. recharts itself is NOT the source of truth — it exposes no runtime
 * contract and its prop types encode none of the semantics we enforce
 * (numeric series, column existence); those live here and in the backend
 * refinement.
 *
 * Mirrors the chat statistics `RichContent` shapes (`ColumnarTable` + per-type
 * config) plus the BI-only `single_value`. When the frontend BI + chat code
 * lands/rebases, their local copies of these types should be replaced by
 * imports from here.
 */

export const WIDGET_TYPES = [
  "single_value",
  "line_chart",
  "bar_chart",
  "pie_chart",
  "table",
  "text",
] as const

export const widgetTypeSchema = z.enum(WIDGET_TYPES)

/**
 * Columnar result table — efficient for transfer (column-aligned). Cells are
 * scalars; a non-scalar (e.g. a JSON object from a `jsonb` column) fails the
 * parse, which is what makes "this result can't render" detectable.
 */
export const columnarTableSchema = z
  .object({
    columns: z.array(z.string()),
    data: z.array(z.array(z.union([z.string(), z.number(), z.null()]))),
  })
  // Every row must be column-aligned: a ragged row (arity ≠ columns.length)
  // is a structurally invalid table no renderer can map by column.
  .refine((t) => t.data.every((row) => row.length === t.columns.length), {
    message: "each data row must have exactly columns.length cells",
    path: ["data"],
  })

/**
 * Column roles a table widget can assign so the renderer styles each column by
 * meaning rather than guessing from its values (STK-808):
 *  - `main`    the principal/identity column (a name, label, category) — the
 *              row's subject. Left-aligned, emphasised.
 *  - `value`   a plain measure (revenue, quantity, weight). Right-aligned,
 *              tabular numerals, `unit`-formatted.
 *  - `delta`   a difference / variation vs a baseline (MoM %, YoY change). Same
 *              numeric alignment as `value`, PLUS a sign-driven colour + arrow
 *              (▲ green for a rise, ▼ red for a drop, muted for no change) and a
 *              signed value ("+12.4 %").
 * A column left without a role falls back to auto-detection (numeric → right).
 */
export const TABLE_COLUMN_ROLES = ["main", "value", "delta"] as const

export const tableColumnRoleSchema = z.enum(TABLE_COLUMN_ROLES)

/**
 * Per-column presentation, DECOUPLED from the SQL the AI writes (STK-808): the
 * SQL keeps terse `snake_case` names, while `label` carries the human-readable
 * header the user sees. This lets the AI rename a column for the reader without
 * touching (and risking) the query.
 */
export const tableColumnSpecSchema = z.object({
  /** The SQL result column this spec styles (must match a selected column). */
  key: z.string(),
  /** Display header shown to the user; falls back to the prettified `key`. */
  label: z.string().optional(),
  /** How to present the column — see `TABLE_COLUMN_ROLES`. */
  role: tableColumnRoleSchema.optional(),
  /** Value/delta unit ("€", "kg", "%") the renderer formats numbers with. */
  unit: z.string().optional(),
})

export const tableConfigSchema = z.object({
  visible_columns: z.array(z.string()).nullish(),
  /**
   * Optional per-column typing + labelling. When present and non-empty it
   * drives WHICH columns show and in WHAT order (superseding `visible_columns`)
   * plus each column's label/role/unit. Omit it for a plain auto-formatted
   * table (the legacy `visible_columns`-only behaviour is unchanged).
   */
  columns: z.array(tableColumnSpecSchema).nullish(),
  /**
   * Names a boolean-ish FLAG column (0/1) the SQL emits to mark summary rows
   * ("Total S26", grand totals…), so the renderer can emphasise them (bold +
   * tint + top border) and pin them out of user sorts (STK-1672). A flag
   * column — rather than row indices — because widget queries are re-run on
   * refresh and a growing result would silently shift indices. The flag column
   * itself is never displayed (excluded from the auto/visible column set; a
   * `columns` spec should simply not list it).
   */
  summary_column: z.string().nullish(),
})

// `unit` is the value-axis unit (e.g. "€", "kg", "%") — a short display string
// the renderer prefixes onto the plotted value (Y-axis ticks + tooltip). It is
// the chart counterpart of `single_value.unit`. There is intentionally no
// per-axis unit: the X axis is the category dimension (rendered as labels, not
// numbers), so only the value axis carries a unit. A multi-series line chart
// shares this one unit across all `y_columns`.
export const barChartConfigSchema = z.object({
  x_column: z.string(),
  y_column: z.string(),
  unit: z.string().optional(),
  /**
   * Grouped/comparison bars: when present (≥2 entries), the renderer draws
   * ONE grouped bar per entry, per category, instead of the single
   * `y_column` bar — e.g. "this week" vs "last week" per top-5 product.
   * `y_column` stays required and unchanged (every existing single-bar
   * widget keeps working with zero migration); when both are set, `y_columns`
   * is authoritative and `y_column` should just repeat `y_columns[0]` to
   * satisfy the required field. Mirrors `lineChartConfigSchema.y_columns`.
   */
  y_columns: z.array(z.string()).min(2).optional(),
  /**
   * Only meaningful when `y_columns` has EXACTLY 2 entries (the "current vs
   * comparison" case). When true, render a per-category delta badge:
   * `(y_columns[0] − y_columns[1]) / y_columns[1]`, signed and color-coded
   * (green for a rise, red for a drop, muted em-dash for a divide-by-zero or
   * null comparison value).
   */
  show_progress: z.boolean().optional(),
})

export const lineChartConfigSchema = z.object({
  x_column: z.string(),
  y_columns: z.array(z.string()).min(1), // multiple columns → multi-series
  unit: z.string().optional(),
  /** When true, render the sum of each y-series over the displayed period
   *  alongside the chart (one total per series for multi-series charts). */
  show_total: z.boolean().optional(),
})

export const pieChartConfigSchema = z.object({
  label_column: z.string(),
  value_column: z.string(),
  unit: z.string().optional(),
})

/**
 * One labelled comparison on a single_value card — the AUTHORING shape stored
 * in the widget config. `column` names a column of the widget's (single-row)
 * query result whose value is compared against the headline value; the renderer
 * computes the signed %Δ. Distinct from the RENDER shape
 * (`singleValueRenderComparisonSchema`), which carries the already-resolved
 * numeric `value`.
 */
/** Max length of a comparison label. The card renders labels on up to TWO short
 *  lines per pill, so a self-evident relative period fits ("semaine dernière",
 *  "il y a 3 sem.", "3 weeks ago") while an over-long essay still can't blow out
 *  the compact layout. Enforced here as a LOUD validation failure at author
 *  time, not a silent UI mess. */
export const SINGLE_VALUE_COMPARISON_LABEL_MAX = 20

export const singleValueComparisonConfigSchema = z.object({
  // AI-authored, in the company's language. A self-evident relative period —
  // keep it within the two-line max below.
  label: z
    .string()
    .min(1)
    .max(
      SINGLE_VALUE_COMPARISON_LABEL_MAX,
      `comparison label must be ≤${SINGLE_VALUE_COMPARISON_LABEL_MAX} chars — a self-evident relative period (e.g. "semaine dernière", "il y a 3 sem.", "3 weeks ago"), not a full sentence`
    ),
  // A column of the query result to compare the headline value against.
  column: z.string().min(1),
  // Optional human-readable period this comparison covers — the exact date or
  // month+year the %Δ is measured against (e.g. "lundi 18 mai 2025", "mai
  // 2025"). Surfaced in the pill's hover tooltip so the short `label` stays
  // terse while the reader can still see precisely what it compares to.
  hint: z.string().optional(),
})

export const singleValueConfigSchema = z.object({
  unit: z.string().optional(),
  // Which result column holds the headline value. Optional: defaults to the
  // first column, preserving the original single-cell single_value.
  value_column: z.string().optional(),
  // Labelled period-over-period comparisons; the renderer computes the signed
  // %Δ of the headline value against each. Generalises the former scalar
  // `deltaPct` (one unlabelled, statically-authored delta) into N labelled
  // comparisons sourced live from the query result.
  comparisons: z.array(singleValueComparisonConfigSchema).optional(),
})

/**
 * A comparison resolved to its value for RENDERING (the adapter projects the
 * config's `column` → its single-row value; the renderer computes the %Δ).
 * `value` is null when the comparison column was absent/null/non-numeric → the
 * card renders a neutral "—" (no percentage, no colour).
 */
export const singleValueRenderComparisonSchema = z.object({
  label: z.string(),
  value: z.number().nullable(),
  // Carried through from the config (see `singleValueComparisonConfigSchema.hint`)
  // → the pill's hover tooltip shows the period the %Δ is measured against.
  hint: z.string().optional(),
})

// A free-text widget: a fixed Markdown `template` (authored in config) whose
// `{{column}}` placeholders are filled from the backing query's SINGLE result
// row — each placeholder resolves to the cell of the like-named column. The
// prose stays static; the SQL only computes the substituted values (e.g. a
// localized date). `valueCount` is the number of values the template expects
// the query to supply (one column per placeholder); the definition carries it
// so compute-time validation can enforce the result's column count matches
// WITHOUT re-parsing the template.
export const textConfigSchema = z.object({
  template: z.string().min(1),
  valueCount: z.number().int().nonnegative(),
})

/** Per-type config schema, keyed by widget type (used to validate `widgets.config`). */
export const widgetConfigSchemas = {
  single_value: singleValueConfigSchema,
  table: tableConfigSchema,
  bar_chart: barChartConfigSchema,
  line_chart: lineChartConfigSchema,
  pie_chart: pieChartConfigSchema,
  text: textConfigSchema,
} as const

/** The render contract: a discriminated union over widget `type`. */
export const widgetSpecSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("single_value"),
    value: z.number(),
    // The render contract carries RESOLVED display fields, not the config's
    // column refs: `value_column`/`comparisons[].column` are projected to
    // `value`/`comparisons[].value` by the adapter, so they don't appear here.
    unit: z.string().optional(),
    comparisons: z.array(singleValueRenderComparisonSchema).optional(),
  }),
  z.object({
    type: z.literal("table"),
    table: columnarTableSchema,
    config: tableConfigSchema,
  }),
  z.object({
    type: z.literal("bar_chart"),
    table: columnarTableSchema,
    config: barChartConfigSchema,
  }),
  z.object({
    type: z.literal("line_chart"),
    table: columnarTableSchema,
    config: lineChartConfigSchema,
  }),
  z.object({
    type: z.literal("pie_chart"),
    table: columnarTableSchema,
    config: pieChartConfigSchema,
  }),
  z.object({
    type: z.literal("text"),
    // The authored Markdown template, placeholders already resolved against
    // `values` at render time (the renderer owns the substitution).
    template: z.string(),
    // Column name → its cell in the query's single result row.
    values: z.record(z.union([z.string(), z.number(), z.null()])),
  }),
])

export type WidgetType = z.infer<typeof widgetTypeSchema>
export type ColumnarTable = z.infer<typeof columnarTableSchema>
export type WidgetSpec = z.infer<typeof widgetSpecSchema>
export type TableConfig = z.infer<typeof tableConfigSchema>
export type TableColumnRole = z.infer<typeof tableColumnRoleSchema>
export type TableColumnSpec = z.infer<typeof tableColumnSpecSchema>
export type BarChartConfig = z.infer<typeof barChartConfigSchema>
export type LineChartConfig = z.infer<typeof lineChartConfigSchema>
export type PieChartConfig = z.infer<typeof pieChartConfigSchema>
export type SingleValueConfig = z.infer<typeof singleValueConfigSchema>
export type SingleValueComparisonConfig = z.infer<
  typeof singleValueComparisonConfigSchema
>
export type SingleValueRenderComparison = z.infer<
  typeof singleValueRenderComparisonSchema
>
export type TextConfig = z.infer<typeof textConfigSchema>
